import { computed, watch, type ComputedRef } from 'vue';
import { storeToRefs } from 'pinia';
import { useMediaQuery } from '@vueuse/core';
import { useViewerModeStore, ViewerLayoutDefault, ViewerMode } from '@/modules/viewer/store/useViewerModeStore';
import { useArtifactsStore } from '@/modules/viewer/store/useArtifactsStore';
import { useViewerItemStore } from '@/modules/viewer/store/useViewerItemStore';
import { useAiMetadataStore } from '@/modules/viewer/store/useAiMetadataStore';
import { useSmartActionsStore } from '@/modules/viewer/store/useSmartActionsStore';
import { useTranscriptionStore } from '@/modules/viewer/store/useTranscriptionStore';
import { useUserStore } from '@/store/useUserStore';

type ResolvableViewerLayout = ViewerMode | ViewerLayoutDefault | null | undefined;

export const isValidViewerMode = (value: unknown): value is ViewerMode => {
  return typeof value === 'string' && (Object.values(ViewerMode) as string[]).includes(value);
};

export const isAvailableViewerMode = (mode: ViewerMode, availableModes: ViewerMode[]): boolean => availableModes.includes(mode);

export const resolveViewerMode = ({
  deepLinkMode,
  savedItemLayout,
  orgDefaultLayout,
  availableModes,
}: {
  deepLinkMode?: ResolvableViewerLayout;
  savedItemLayout?: ResolvableViewerLayout;
  orgDefaultLayout?: ResolvableViewerLayout;
  availableModes: ViewerMode[];
}): ViewerMode => {
  const candidates = [deepLinkMode, savedItemLayout, orgDefaultLayout].filter(
    (mode): mode is ViewerMode => isValidViewerMode(mode) && isAvailableViewerMode(mode, availableModes)
  );

  if (candidates.length > 0) {
    return candidates[0];
  }

  if (availableModes.includes(ViewerMode.LOGS)) {
    return ViewerMode.LOGS;
  }
  if (availableModes.includes(ViewerMode.SUMMARY)) {
    return ViewerMode.SUMMARY;
  }
  return ViewerMode.VIDEO;
};

/**
 * Computes which viewer modes should be available, driven by server-side
 * state (logs availability via the artifacts store). Logs stays selectable
 * on mobile too — the layout renders a "switch to desktop" notice there
 * instead of the real log table, rather than hiding the mode entirely.
 *
 * Summary is available whenever the item has summary content in its
 * description, or while that content is actively being (re)generated — the
 * latter only when the viewing user hasn't turned AI summaries off entirely.
 * It remains hidden for items with neither.
 *
 * Usage: call once at the top of a layout component.
 */
export const useViewerAvailableModes = ({ syncCurrentMode = false }: { syncCurrentMode?: boolean } = {}): {
  availableModes: ComputedRef<ViewerMode[]>;
  isMobile: ComputedRef<boolean>;
} => {
  const viewerModeStore = useViewerModeStore();
  const { currentMode } = storeToRefs(viewerModeStore);
  const { insightsURL } = storeToRefs(useArtifactsStore());
  const { description } = storeToRefs(useViewerItemStore());
  const { isProcessing: isAiMetadataProcessing } = storeToRefs(useAiMetadataStore());
  const { isProcessing: isSmartActionProcessing } = storeToRefs(useSmartActionsStore());
  const { isTranscriptionProcessing } = storeToRefs(useTranscriptionStore());
  const { smartActionSetting } = storeToRefs(useUserStore());

  const isMobile = useMediaQuery('(max-width: 1023px)');
  const hasLogs = computed(() => insightsURL.value !== '');
  // Available once there's content to show, OR while something is actively
  // generating it (SummaryContent renders its own in-place processing state
  // for that case — the mode itself shouldn't disappear mid-regeneration,
  // which previously bounced the viewer to Video and hid the mode dropdown
  // the moment a smart action was (re)requested from Summary mode).
  //
  // Before any content exists, the "actively generating" branch is skipped
  // when the viewing user's own AI-summary preference is 'never' — right
  // after recording, categorization briefly reports processing regardless
  // of that preference, and without this check Summary would flash on then
  // bounce back to Video once it settles to "nothing to generate". This
  // preference is scoped to the *viewing* user (there's no owner-scoped
  // equivalent exposed to the frontend), so it's only reliably correct for
  // this exact scenario — the item's own owner viewing their fresh upload.
  const hasSummary = computed(() => {
    if (description.value?.length) return true;
    if (smartActionSetting.value === 'never') return false;
    return isAiMetadataProcessing.value || isSmartActionProcessing.value || isTranscriptionProcessing.value;
  });

  const availableModes = computed<ViewerMode[]>(() => {
    const modes: ViewerMode[] = [ViewerMode.VIDEO];
    if (hasSummary.value) {
      modes.push(ViewerMode.SUMMARY);
    }
    if (hasLogs.value) {
      modes.push(ViewerMode.LOGS);
    }
    return modes;
  });

  // The layout owns this fallback watcher. Consumers that only need the
  // available-mode list (such as URL synchronization) opt out to avoid
  // registering a second watcher for the same availability change.
  if (syncCurrentMode) {
    watch(
      availableModes,
      modes => {
        if (!modes.includes(currentMode.value)) {
          viewerModeStore.setMode(resolveViewerMode({ availableModes: modes }));
        }
      },
      { immediate: true }
    );
  }

  return { availableModes, isMobile: computed(() => isMobile.value) };
};
